import React, { useState, useEffect, useCallback } from 'react';
import { tradingApi, Notification } from '../services/api';
import { Bell, Check, AlertCircle, Info, TrendingUp, ShoppingBag, X, Settings } from 'lucide-react';
import { formatTimeET } from '../utils/datetime';
import LoadingSpinner from './LoadingSpinner';

const NotificationFeed: React.FC<{
    onClose?: () => void;
    onUnreadCountChange?: (count: number) => void;
}> = ({ onClose, onUnreadCountChange }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        notify_email_signal: false,
        notify_email_fill: true,
        notify_email_error: true,
        notify_email_warning: true,
        notify_email_system: false,
        notify_sound_enabled: true,
    });
    const [savingPrefs, setSavingPrefs] = useState(false);

    const [activeFilter, setActiveFilter] = useState<'ALL' | 'SIGNAL' | 'FILL' | 'ERROR' | 'WARNING'>('ALL');

    const fetchData = useCallback(async () => {
        try {
            const [notifRes, countRes, contextRes] = await Promise.all([
                tradingApi.getNotifications(50), // Fetch more for filtering
                tradingApi.getUnreadCount(),
                tradingApi.getCurrentContext()
            ]);

            if (notifRes.success) setNotifications(notifRes.data);
            if (countRes.success) setUnreadCount(countRes.data.count);
            if (contextRes.success && contextRes.data) {
                setPreferences(contextRes.data.preferences);
            }
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (onUnreadCountChange) {
            onUnreadCountChange(unreadCount);
        }
    }, [unreadCount, onUnreadCountChange]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 15000); // Poll every 15s
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleTogglePref = async (key: keyof typeof preferences) => {
        setSavingPrefs(true);
        const newPrefs = { ...preferences, [key]: !preferences[key] };
        setPreferences(newPrefs); // Optimistic wrap
        try {
            await tradingApi.updateNotificationPreferences({ [key]: newPrefs[key] });
        } catch {
            // Revert on error
            setPreferences(preferences);
        } finally {
            setSavingPrefs(false);
        }
    };

    const handleMarkRead = async (id: number) => {
        try {
            const res = await tradingApi.markNotificationRead(id);
            if (res.success) {
                setNotifications(prev =>
                    prev.map(n => n.id === id ? { ...n, is_read: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const res = await tradingApi.markAllRead();
            if (res.success) {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
            }
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'SIGNAL': return <TrendingUp className="w-4 h-4 text-blue-400" />;
            case 'FILL': return <ShoppingBag className="w-4 h-4 text-emerald-400" />;
            case 'ERROR': return <AlertCircle className="w-4 h-4 text-red-500" />;
            case 'WARNING': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
            default: return <Info className="w-4 h-4 text-muted" />;
        }
    };

    const getPriorityClass = (priority: string) => {
        switch (priority) {
            case 'success': return 'border-emerald-500/30 bg-emerald-500/5';
            case 'error': return 'border-red-500/30 bg-red-500/5';
            case 'warning': return 'border-yellow-500/30 bg-yellow-500/5';
            default: return 'border-white/10 bg-white/5';
        }
    };

    const filteredNotifications = notifications.filter(n =>
        activeFilter === 'ALL' || n.type === activeFilter
    );

    const getFilterButton = (filter: typeof activeFilter, label: string) => (
        <button
            onClick={() => setActiveFilter(filter)}
            className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all duration-200 border ${activeFilter === filter
                ? 'bg-neon text-black border-neon shadow-[0_0_15px_rgba(92,246,205,0.4)]'
                : 'bg-white/5 text-muted border-white/10 hover:text-white hover:bg-white/10'
                }`}
        >
            {label}
        </button>
    );

    return (
        <div className="flex flex-col h-full bg-surface/95 backdrop-blur-2xl border-l border-white/10 shadow-[-20px_0_40px_rgba(0,0,0,0.5)]">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5 bg-white/5">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Bell className="w-5 h-5 text-neon" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </div>
                    <h2 className="text-sm font-bold uppercase tracking-widest text-white">Activity</h2>
                </div>
                <div className="flex items-center gap-4">
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="text-[10px] font-bold uppercase tracking-tighter text-neon hover:text-white transition-colors flex items-center gap-1 group"
                        >
                            <Check className="w-3 h-3 group-hover:scale-110 transition-transform" />
                            Clear
                        </button>
                    )}
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-1.5 rounded-lg hover:bg-white/10 transition ${showSettings ? 'text-neon bg-white/5' : 'text-muted hover:text-white'}`}
                        title="Email Preferences"
                    >
                        <Settings className="w-5 h-5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-muted hover:text-white transition"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Filter Bar */}
            {!showSettings && (
                <div className="flex items-center gap-2 p-4 border-b border-white/5 bg-black/10 overflow-x-auto no-scrollbar">
                    {getFilterButton('ALL', 'All')}
                    {getFilterButton('SIGNAL', 'Signals')}
                    {getFilterButton('FILL', 'Trades')}
                    {getFilterButton('WARNING', 'Warnings')}
                    {getFilterButton('ERROR', 'Errors')}
                </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
                <div className="p-5 border-b border-white/5 bg-black/20">
                    <h3 className="text-[10px] font-bold text-white uppercase tracking-widest mb-4 opacity-50">Email Alert Preferences</h3>
                    <div className="space-y-4">
                        <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-xs font-semibold text-muted group-hover:text-white transition">Trade Signals</span>
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={preferences.notify_email_signal} onChange={() => handleTogglePref('notify_email_signal')} disabled={savingPrefs} />
                                <div className={`w-8 h-4 bg-white/10 rounded-full transition-colors ${preferences.notify_email_signal ? 'bg-neon' : ''}`}></div>
                                <div className={`dot absolute left-[3px] top-[3px] w-2.5 h-2.5 rounded-full transition-transform transform ${preferences.notify_email_signal ? 'translate-x-[16px] bg-black shadow-[0_0_8px_rgba(92,246,205,0.8)]' : 'bg-muted'}`}></div>
                            </div>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-xs font-semibold text-muted group-hover:text-white transition">Order Fills</span>
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={preferences.notify_email_fill} onChange={() => handleTogglePref('notify_email_fill')} disabled={savingPrefs} />
                                <div className={`w-8 h-4 bg-white/10 rounded-full transition-colors ${preferences.notify_email_fill ? 'bg-neon' : ''}`}></div>
                                <div className={`dot absolute left-[3px] top-[3px] w-2.5 h-2.5 rounded-full transition-transform transform ${preferences.notify_email_fill ? 'translate-x-[16px] bg-black shadow-[0_0_8px_rgba(92,246,205,0.8)]' : 'bg-muted'}`}></div>
                            </div>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-xs font-semibold text-muted group-hover:text-white transition">Order Errors (Critical)</span>
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={preferences.notify_email_error} onChange={() => handleTogglePref('notify_email_error')} disabled={savingPrefs} />
                                <div className={`w-8 h-4 bg-white/10 rounded-full transition-colors ${preferences.notify_email_error ? 'bg-neon' : ''}`}></div>
                                <div className={`dot absolute left-[3px] top-[3px] w-2.5 h-2.5 rounded-full transition-transform transform ${preferences.notify_email_error ? 'translate-x-[16px] bg-black shadow-[0_0_8px_rgba(92,246,205,0.8)]' : 'bg-muted'}`}></div>
                            </div>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-xs font-semibold text-muted group-hover:text-white transition">Data Quality Warnings</span>
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={preferences.notify_email_warning} onChange={() => handleTogglePref('notify_email_warning')} disabled={savingPrefs} />
                                <div className={`w-8 h-4 bg-white/10 rounded-full transition-colors ${preferences.notify_email_warning ? 'bg-neon' : ''}`}></div>
                                <div className={`dot absolute left-[3px] top-[3px] w-2.5 h-2.5 rounded-full transition-transform transform ${preferences.notify_email_warning ? 'translate-x-[16px] bg-black shadow-[0_0_8px_rgba(92,246,205,0.8)]' : 'bg-muted'}`}></div>
                            </div>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-xs font-semibold text-muted group-hover:text-white transition">System Status</span>
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={preferences.notify_email_system} onChange={() => handleTogglePref('notify_email_system')} disabled={savingPrefs} />
                                <div className={`w-8 h-4 bg-white/10 rounded-full transition-colors ${preferences.notify_email_system ? 'bg-neon' : ''}`}></div>
                                <div className={`dot absolute left-[3px] top-[3px] w-2.5 h-2.5 rounded-full transition-transform transform ${preferences.notify_email_system ? 'translate-x-[16px] bg-black shadow-[0_0_8px_rgba(92,246,205,0.8)]' : 'bg-muted'}`}></div>
                            </div>
                        </label>
                        <div className="h-px bg-white/10 my-1" />
                        <label className="flex items-center justify-between cursor-pointer group">
                            <span className="text-xs font-semibold text-neon group-hover:text-white transition">Sound Alerts</span>
                            <div className="relative">
                                <input type="checkbox" className="sr-only" checked={preferences.notify_sound_enabled} onChange={() => handleTogglePref('notify_sound_enabled')} disabled={savingPrefs} />
                                <div className={`w-8 h-4 bg-white/10 rounded-full transition-colors ${preferences.notify_sound_enabled ? 'bg-neon' : ''}`}></div>
                                <div className={`dot absolute left-[3px] top-[3px] w-2.5 h-2.5 rounded-full transition-transform transform ${preferences.notify_sound_enabled ? 'translate-x-[16px] bg-black shadow-[0_0_8px_rgba(92,246,205,0.8)]' : 'bg-black'}`}></div>
                            </div>
                        </label>
                    </div>
                </div>
            )}

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3">
                {loading && notifications.length === 0 ? (
                    <div className="flex justify-center p-8">
                        <LoadingSpinner size="md" />
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-muted gap-4 opacity-30 pb-20">
                        <Info className="w-12 h-12" />
                        <span className="text-sm font-medium tracking-wide">
                            {activeFilter === 'ALL' ? 'No recent events' : `No ${activeFilter.toLowerCase()} events`}
                        </span>
                    </div>
                ) : (
                    filteredNotifications.map((n) => (
                        <div
                            key={n.id}
                            onClick={() => !n.is_read && handleMarkRead(n.id)}
                            className={`relative group cursor-pointer p-4 rounded-xl border transition-all duration-200 hover:shadow-lg ${getPriorityClass(n.priority)} ${!n.is_read ? 'ring-1 ring-neon/20' : 'opacity-70 hover:opacity-100'}`}
                        >
                            {/* Unread indicator */}
                            {!n.is_read && (
                                <div className="absolute top-4 right-4 w-2 h-2 bg-neon rounded-full shadow-[0_0_8px_rgba(var(--neon-rgb),0.5)]" />
                            )}

                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 rounded-lg bg-surface/50 border border-white/5">
                                            {getIcon(n.type)}
                                        </div>
                                        <span className="text-[10px] font-black text-muted uppercase tracking-widest">{n.type}</span>
                                    </div>
                                    <span className="text-[10px] font-mono text-muted/60">{formatTimeET(n.timestamp)}</span>
                                </div>
                                <div className="space-y-1.5">
                                    <h3 className={`text-xs font-bold leading-tight ${n.is_read ? 'text-white/80' : 'text-white'}`}>{n.title}</h3>
                                    <p className="text-[11px] leading-relaxed text-white/50 italic">{n.message}</p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/5 text-center">
                <p className="text-[10px] text-muted tracking-widest uppercase opacity-30">ALGO TRADER NOTIFICATION ENGINE</p>
            </div>
        </div>
    );
};

export default NotificationFeed;
